/**
 * HubSpot adapter.
 *
 * Two transports:
 *   - 'proxy' (default in browser): every call hits `/.netlify/functions/crm-proxy`.
 *     The proxy decrypts tokens server-side, talks to HubSpot, returns parsed JSON.
 *   - 'direct' (server-only): used inside Netlify Functions where we already have
 *     a decrypted access token (e.g. webhook handler doing a follow-up read).
 *
 * Endpoints used (HubSpot v3):
 *   GET  /crm/v3/objects/deals
 *   POST /crm/v3/objects/deals/search          (when filterId provided)
 *   POST /crm/v3/objects/deals
 *   PATCH /crm/v3/objects/deals/{dealId}
 *   GET  /crm/v3/owners
 *   GET  /crm/v3/pipelines/deals
 *   GET  /crm/v3/properties/deals  /  /crm/v3/properties/contacts
 *
 * Pagination: HubSpot returns `paging.next.after` — we surface it as `nextCursor`.
 * Rate limits: 100 req / 10s per portal (free), 110 req / 10s (paid). The proxy
 * surfaces 429s as AdapterError(RATE_LIMITED) — callers should backoff.
 */

import {
  type CrmAdapter,
  AdapterError,
  classifyHttpError,
} from './adapter';
import { getProxyAuthHeader } from './proxy-auth';
import type {
  Contact,
  CreateContactInput,
  CreateDealInput,
  CrmFilter,
  CrmObjectType,
  CustomField,
  Deal,
  ListContactsOpts,
  ListContactsResult,
  ListDealsOpts,
  ListDealsResult,
  ListLeadsOpts,
  ListLeadsResult,
  ListMeetingsOpts,
  ListMeetingsResult,
  Owner,
  UpdateContactInput,
  UpdateDealPatch,
} from './types';

const HUBSPOT_API_BASE = 'https://api.hubapi.com';

const CONTACT_PROPERTIES_TO_FETCH = [
  'firstname',
  'lastname',
  'email',
  'phone',
  'company',
  'address',
  'city',
  'zip',
  'lifecyclestage',
  'v9_map_status',
  'v9_lead_source',
  'createdate',
  'lastmodifieddate',
];

const KNOWN_CONTACT_PROPS = new Set(CONTACT_PROPERTIES_TO_FETCH);

const DEAL_PROPERTIES_TO_FETCH = [
  'dealname',
  'amount',
  'dealstage',
  'pipeline',
  'hubspot_owner_id',
  'closedate',
  'createdate',
  'hs_lastmodifieddate',
  // Best-effort address bag (HubSpot doesn't put address on deals natively, but
  // many clients copy it across via workflow):
  'property_address',
  'address',
  'city',
  'state',
  'zip',
  'country',
];

type HubSpotTransport =
  | { mode: 'proxy' }
  | { mode: 'direct'; accessToken: string };

interface HubSpotDealRecord {
  id: string;
  properties: Record<string, string | null>;
  createdAt?: string;
  updatedAt?: string;
  associations?: {
    contacts?: { results: { id: string; type: string }[] };
  };
}

interface HubSpotPagedResponse<T> {
  results: T[];
  paging?: { next?: { after: string } };
}

interface HubSpotContact {
  id: string;
  properties: Record<string, string | null>;
}

interface HubSpotPipeline {
  id: string;
  label: string;
  stages: { id: string; label: string }[];
}

interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  options?: { label: string; value: string }[];
}

export class HubSpotAdapter implements CrmAdapter {
  readonly type = 'hubspot' as const;
  private transport: HubSpotTransport;
  /** Cached pipeline → stages so we don't re-hit /pipelines/deals every listDeals call. */
  private stageNameCache: Map<string, string> | null = null;

  constructor(transport: HubSpotTransport = { mode: 'proxy' }) {
    this.transport = transport;
  }

  // ── Public adapter surface ───────────────────────────────────────────────

  async listDeals(opts: ListDealsOpts = {}): Promise<ListDealsResult> {
    const { filterId, pageSize = 25, cursor } = opts;
    const stageNames = await this.getStageNames();

    let raw: HubSpotPagedResponse<HubSpotDealRecord>;

    if (filterId) {
      // HubSpot "views" are server-side stored filters reachable via the search endpoint.
      // Most clients won't have these wired; we still implement the endpoint shape.
      raw = await this.call<HubSpotPagedResponse<HubSpotDealRecord>>(
        'POST',
        '/crm/v3/objects/deals/search',
        {
          limit: pageSize,
          after: cursor,
          properties: DEAL_PROPERTIES_TO_FETCH,
          filterGroups: [{ filters: [{ propertyName: 'hs_view_id', operator: 'EQ', value: filterId }] }],
        },
      );
    } else {
      const qs = new URLSearchParams({
        limit: String(pageSize),
        properties: DEAL_PROPERTIES_TO_FETCH.join(','),
        associations: 'contacts',
        archived: 'false',
      });
      if (cursor) qs.set('after', cursor);
      raw = await this.call<HubSpotPagedResponse<HubSpotDealRecord>>(
        'GET',
        `/crm/v3/objects/deals?${qs.toString()}`,
      );
    }

    // Resolve contacts for every deal in one batched read.
    const contactIds = new Set<string>();
    for (const deal of raw.results) {
      const first = deal.associations?.contacts?.results?.[0];
      if (first) contactIds.add(first.id);
    }
    const contactMap = await this.fetchContacts([...contactIds]);

    const items = raw.results.map((d) => this.normaliseDeal(d, stageNames, contactMap));

    return {
      items,
      nextCursor: raw.paging?.next?.after ?? null,
    };
  }

  async getDeal(id: string): Promise<Deal | null> {
    const qs = new URLSearchParams({
      properties: DEAL_PROPERTIES_TO_FETCH.join(','),
      associations: 'contacts',
      archived: 'false',
    });
    try {
      const raw = await this.call<HubSpotDealRecord>(
        'GET',
        `/crm/v3/objects/deals/${id}?${qs.toString()}`,
      );
      const stageNames = await this.getStageNames();
      const contactIds = new Set<string>();
      const first = raw.associations?.contacts?.results?.[0];
      if (first) contactIds.add(first.id);
      const contactMap = await this.fetchContacts([...contactIds]);
      return this.normaliseDeal(raw, stageNames, contactMap);
    } catch (err) {
      if (err instanceof AdapterError && err.code === 'NOT_FOUND') return null;
      throw err;
    }
  }

  async listOwners(): Promise<Owner[]> {
    const raw = await this.call<HubSpotPagedResponse<{
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
    }>>('GET', '/crm/v3/owners?limit=100');
    return raw.results.map((o) => ({
      id: o.id,
      email: o.email,
      firstName: o.firstName ?? '',
      lastName: o.lastName ?? '',
    }));
  }

  async listFilters(): Promise<CrmFilter[]> {
    // HubSpot doesn't expose user-created list views via public API. We approximate
    // by exposing pipelines as "filters" — each pipeline is a meaningful slice of deals.
    const pipelines = await this.call<HubSpotPagedResponse<HubSpotPipeline>>(
      'GET',
      '/crm/v3/pipelines/deals',
    );
    return pipelines.results.map((p) => ({
      id: p.id,
      name: `Pipeline: ${p.label}`,
      objectType: 'deal' as const,
    }));
  }

  async listCustomFields(objectType: CrmObjectType): Promise<CustomField[]> {
    const path = objectType === 'deal' ? '/crm/v3/properties/deals' : '/crm/v3/properties/contacts';
    const raw = await this.call<HubSpotPagedResponse<HubSpotProperty>>('GET', path);
    return raw.results.map((p) => ({
      name: p.name,
      label: p.label,
      type: this.mapPropertyType(p.type),
      options: p.options?.map((o) => o.value),
    }));
  }

  async createDeal(input: CreateDealInput): Promise<Deal> {
    const properties: Record<string, string> = {
      dealname: input.name,
    };
    if (typeof input.amount === 'number') properties.amount = String(input.amount);
    if (input.stageId) properties.dealstage = input.stageId;
    if (input.customFields) {
      for (const [k, v] of Object.entries(input.customFields)) {
        if (v === null || v === undefined) continue;
        properties[k] = typeof v === 'string' ? v : JSON.stringify(v);
      }
    }
    const raw = await this.call<HubSpotDealRecord>(
      'POST',
      '/crm/v3/objects/deals',
      { properties },
    );
    // If a contactEmail was supplied, look up the contact and associate it with
    // the new deal. Best-effort: failures here don't roll back the deal create.
    let contactMap = new Map<string, HubSpotContact>();
    if (input.contactEmail) {
      try {
        const search = await this.call<{ results: { id: string }[] }>(
          'POST',
          '/crm/v3/objects/contacts/search',
          {
            limit: 1,
            filterGroups: [{ filters: [{ propertyName: 'email', operator: 'EQ', value: input.contactEmail }] }],
          },
        );
        const contactId = search.results[0]?.id;
        if (contactId) {
          await this.call<unknown>(
            'PUT',
            `/crm/v3/objects/deals/${raw.id}/associations/contacts/${contactId}/deal_to_contact`,
          );
          // Seed the contact into the map and record the association on the create
          // response so normaliseDeal can populate contactName/contactPhone/address.
          // The POST /deals response doesn't echo associations, so we attach it here.
          contactMap = await this.fetchContacts([contactId]);
          raw.associations = {
            ...raw.associations,
            contacts: { results: [{ id: contactId, type: 'deal_to_contact' }] },
          };
        }
      } catch {
        // Swallow — association failure is non-fatal.
      }
    }
    const stageNames = await this.getStageNames();
    return this.normaliseDeal(raw, stageNames, contactMap);
  }

  async updateDeal(id: string, patch: UpdateDealPatch): Promise<Deal> {
    const properties: Record<string, string> = {};
    if (patch.name !== undefined) properties.dealname = patch.name;
    if (patch.amount !== undefined) properties.amount = String(patch.amount);
    if (patch.stageId !== undefined) properties.dealstage = patch.stageId;
    await this.call<HubSpotDealRecord>(
      'PATCH',
      `/crm/v3/objects/deals/${id}`,
      { properties },
    );
    // The PATCH response omits associations, so re-read the deal via getDeal —
    // it fetches stageNames + associated contacts so contactName/contactPhone/
    // address survive the update instead of coming back null.
    return (await this.getDeal(id))!;
  }

  async assignOwner(dealId: string, ownerId: string): Promise<void> {
    await this.call<HubSpotDealRecord>(
      'PATCH',
      `/crm/v3/objects/deals/${dealId}`,
      { properties: { hubspot_owner_id: ownerId } },
    );
  }

  async listMeetings(opts: ListMeetingsOpts = {}): Promise<ListMeetingsResult> {
    const pageSize = opts.pageSize ?? 100;
    // HubSpot meetings = engagements with type=MEETING in v3. Use the
    // dedicated /objects/meetings endpoint and pull the standard properties.
    const properties = [
      'hs_meeting_title',
      'hs_meeting_body',
      'hs_meeting_start_time',
      'hs_meeting_end_time',
      'hs_meeting_location',
      'hs_meeting_external_url',
      'hs_internal_meeting_notes',
      'hs_timestamp',
    ];
    const qs = new URLSearchParams({
      limit: String(pageSize),
      properties: properties.join(','),
      archived: 'false',
    });
    let raw: HubSpotPagedResponse<HubSpotDealRecord>;
    try {
      raw = await this.call<HubSpotPagedResponse<HubSpotDealRecord>>(
        'GET',
        `/crm/v3/objects/meetings?${qs.toString()}`,
      );
    } catch (err) {
      // If the portal doesn't have meetings access (404/403), return empty
      // rather than blowing up the entire scheduled job.
      if (err instanceof AdapterError && (err.code === 'NOT_FOUND' || err.code === 'PERMISSION_DENIED')) {
        return { items: [] };
      }
      throw err;
    }

    const fromMs = opts.fromIso ? Date.parse(opts.fromIso) : Number.NEGATIVE_INFINITY;
    const toMs = opts.toIso ? Date.parse(opts.toIso) : Number.POSITIVE_INFINITY;

    const items = raw.results
      .map((m) => {
        const p = m.properties ?? {};
        const startRaw = p.hs_meeting_start_time ?? p.hs_timestamp;
        if (!startRaw) return null;
        const startMs = Date.parse(startRaw);
        if (!Number.isFinite(startMs)) return null;
        const endRaw = p.hs_meeting_end_time;
        const endMs = endRaw && Number.isFinite(Date.parse(endRaw))
          ? Date.parse(endRaw)
          : startMs + 30 * 60_000;
        return {
          id: m.id,
          title: p.hs_meeting_title ?? '(untitled meeting)',
          startAt: new Date(startMs).toISOString(),
          endAt: new Date(endMs).toISOString(),
          location: p.hs_meeting_location ?? null,
          attendees: [] as string[],
          url: p.hs_meeting_external_url ?? null,
          // Defensive: deep-copy properties so callers can't mutate adapter state.
          raw: { ...p },
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .filter((x) => {
        const s = Date.parse(x.startAt);
        return s >= fromMs && s <= toMs;
      });

    return { items };
  }

  // ── Contact CRUD ─────────────────────────────────────────────────────────

  async listContacts(opts: ListContactsOpts = {}): Promise<ListContactsResult> {
    const { cursor, limit = 25 } = opts;
    const qs = new URLSearchParams({
      limit: String(limit),
      properties: CONTACT_PROPERTIES_TO_FETCH.join(','),
      archived: 'false',
    });
    if (cursor) qs.set('after', cursor);
    const raw = await this.call<HubSpotPagedResponse<HubSpotContact>>(
      'GET',
      `/crm/v3/objects/contacts?${qs.toString()}`,
    );
    const items = raw.results.map((c) => this.normaliseContact(c));
    return {
      items,
      nextCursor: raw.paging?.next?.after ?? null,
    };
  }

  async getContact(id: string): Promise<Contact | null> {
    const qs = new URLSearchParams({
      properties: CONTACT_PROPERTIES_TO_FETCH.join(','),
    });
    try {
      const raw = await this.call<HubSpotContact>(
        'GET',
        `/crm/v3/objects/contacts/${id}?${qs.toString()}`,
      );
      return this.normaliseContact(raw);
    } catch (err) {
      if (err instanceof AdapterError && err.code === 'NOT_FOUND') return null;
      throw err;
    }
  }

  async createContact(input: CreateContactInput): Promise<Contact> {
    const properties = this.contactInputToProperties(input);
    const raw = await this.call<HubSpotContact>(
      'POST',
      '/crm/v3/objects/contacts',
      { properties },
    );
    return this.normaliseContact(raw);
  }

  async updateContact(id: string, patch: UpdateContactInput): Promise<Contact> {
    const properties = this.contactInputToProperties(patch);
    const raw = await this.call<HubSpotContact>(
      'PATCH',
      `/crm/v3/objects/contacts/${id}`,
      { properties },
    );
    return this.normaliseContact(raw);
  }

  async listLeads(opts: ListLeadsOpts = {}): Promise<ListLeadsResult> {
    const { cursor, limit = 25 } = opts;
    const stages = opts.stages ?? ['subscriber', 'lead', 'marketingqualifiedlead'];
    const raw = await this.call<HubSpotPagedResponse<HubSpotContact>>(
      'POST',
      '/crm/v3/objects/contacts/search',
      {
        filterGroups: [
          {
            filters: [
              {
                propertyName: 'lifecyclestage',
                operator: 'IN',
                values: stages,
              },
            ],
          },
        ],
        properties: CONTACT_PROPERTIES_TO_FETCH,
        limit,
        after: cursor,
      },
    );
    const items = raw.results.map((c) => this.normaliseContact(c));
    return {
      items,
      nextCursor: raw.paging?.next?.after ?? null,
    };
  }

  private contactInputToProperties(input: CreateContactInput | UpdateContactInput): Record<string, string> {
    const properties: Record<string, string> = {};
    if (input.firstName !== undefined) properties.firstname = input.firstName ?? '';
    if (input.lastName !== undefined) properties.lastname = input.lastName ?? '';
    if (input.email !== undefined) properties.email = input.email ?? '';
    if (input.phone !== undefined) properties.phone = input.phone ?? '';
    if (input.company !== undefined) properties.company = input.company ?? '';
    if (input.customFields) {
      for (const [k, v] of Object.entries(input.customFields)) {
        if (v === null || v === undefined) continue;
        properties[k] = typeof v === 'string' ? v : JSON.stringify(v);
      }
    }
    return properties;
  }

  private normaliseContact(c: HubSpotContact): Contact {
    const p = c.properties ?? {};
    const customFields: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(p)) {
      if (!KNOWN_CONTACT_PROPS.has(k)) customFields[k] = v;
    }
    // Compose address from address, city, zip; null if all empty.
    const addressParts = [p.address, p.city, p.zip].filter(Boolean);
    const address = addressParts.length > 0 ? addressParts.join(', ') : null;
    return {
      id: c.id,
      firstName: p.firstname ?? null,
      lastName: p.lastname ?? null,
      email: p.email ?? null,
      phone: p.phone ?? null,
      company: p.company ?? null,
      lifecycleStage: p.lifecyclestage ?? null,
      address,
      customFields,
      createdAt: new Date(p.createdate ?? Date.now()),
      updatedAt: new Date(p.lastmodifieddate ?? Date.now()),
    };
  }

  async setCustomField(dealId: string, fieldName: string, value: unknown): Promise<void> {
    const stringValue =
      value === null || value === undefined
        ? ''
        : typeof value === 'string'
          ? value
          : typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : JSON.stringify(value);
    await this.call<HubSpotDealRecord>(
      'PATCH',
      `/crm/v3/objects/deals/${dealId}`,
      { properties: { [fieldName]: stringValue } },
    );
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private async fetchContacts(ids: string[]): Promise<Map<string, HubSpotContact>> {
    if (ids.length === 0) return new Map();
    const raw = await this.call<{ results: HubSpotContact[] }>(
      'POST',
      '/crm/v3/objects/contacts/batch/read',
      {
        properties: ['firstname', 'lastname', 'phone', 'email', 'address', 'city', 'state', 'zip'],
        inputs: ids.map((id) => ({ id })),
      },
    );
    return new Map(raw.results.map((c) => [c.id, c]));
  }

  private async getStageNames(): Promise<Map<string, string>> {
    if (this.stageNameCache) return this.stageNameCache;
    const pipelines = await this.call<HubSpotPagedResponse<HubSpotPipeline>>(
      'GET',
      '/crm/v3/pipelines/deals',
    );
    const cache = new Map<string, string>();
    for (const p of pipelines.results) {
      for (const stage of p.stages) {
        cache.set(stage.id, stage.label);
      }
    }
    this.stageNameCache = cache;
    return cache;
  }

  private normaliseDeal(
    d: HubSpotDealRecord,
    stageNames: Map<string, string>,
    contactMap: Map<string, HubSpotContact>,
  ): Deal {
    const p = d.properties ?? {};
    const stageId = p.dealstage ?? null;
    const contactId = d.associations?.contacts?.results?.[0]?.id;
    const contact = contactId ? contactMap.get(contactId) : undefined;
    const cp = contact?.properties ?? {};

    const knownDealProps = new Set(DEAL_PROPERTIES_TO_FETCH);
    const customFields: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(p)) {
      if (!knownDealProps.has(k)) customFields[k] = v;
    }

    const dealAddress =
      [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ') || null;
    const contactAddress =
      [cp.address, cp.city, cp.state, cp.zip].filter(Boolean).join(', ') || null;

    const amount = p.amount != null && p.amount !== '' ? Number(p.amount) : null;

    return {
      id: d.id,
      name: p.dealname ?? '',
      amount: Number.isFinite(amount as number) ? amount : null,
      stageId,
      stageName: stageId ? (stageNames.get(stageId) ?? null) : null,
      ownerId: p.hubspot_owner_id ?? null,
      address: dealAddress ?? contactAddress,
      contactName:
        [cp.firstname, cp.lastname].filter(Boolean).join(' ') || null,
      contactPhone: cp.phone ?? null,
      customFields,
      createdAt: new Date(p.createdate ?? d.createdAt ?? Date.now()),
      updatedAt: new Date(p.hs_lastmodifieddate ?? d.updatedAt ?? Date.now()),
    };
  }

  private mapPropertyType(t: string): CustomField['type'] {
    if (t === 'number') return 'number';
    if (t === 'enumeration') return 'enum';
    if (t === 'date' || t === 'datetime') return 'date';
    return 'string';
  }

  // ── HTTP transport ───────────────────────────────────────────────────────

  private async call<T>(method: 'GET' | 'POST' | 'PATCH' | 'PUT', path: string, body?: unknown): Promise<T> {
    if (this.transport.mode === 'direct') {
      return this.callDirect<T>(method, path, body, this.transport.accessToken);
    }
    return this.callProxy<T>(method, path, body);
  }

  private async callDirect<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT',
    path: string,
    body: unknown,
    accessToken: string,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${HUBSPOT_API_BASE}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new AdapterError('NETWORK_ERROR', `[hubspot] fetch failed: ${(err as Error).message}`);
    }
    if (!res.ok) {
      const text = await safeText(res);
      throw new AdapterError(classifyHttpError(res.status, text), `[hubspot] ${res.status} ${path}`, {
        status: res.status,
        upstreamBody: text,
      });
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private async callProxy<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT',
    path: string,
    body: unknown,
  ): Promise<T> {
    const authHeader = await getProxyAuthHeader();
    let res: Response;
    try {
      res = await fetch('/.netlify/functions/crm-proxy', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeader },
        body: JSON.stringify({ provider: 'hubspot', method, path, body }),
      });
    } catch (err) {
      throw new AdapterError('NETWORK_ERROR', `[hubspot-proxy] ${(err as Error).message}`);
    }
    if (!res.ok) {
      const text = await safeText(res);
      throw new AdapterError(
        res.status === 401 ? 'NOT_AUTHENTICATED' : classifyHttpError(res.status, text),
        `[hubspot-proxy] ${res.status}`,
        { status: res.status, upstreamBody: text },
      );
    }
    return (await res.json()) as T;
  }
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}

/**
 * Verify a HubSpot v3 webhook signature.
 *
 * HubSpot signs each request with HMAC-SHA-256 over:
 *   {method}{rawUrl}{rawBody}{timestamp}
 * The signature is in `X-HubSpot-Signature-v3`, timestamp in `X-HubSpot-Request-Timestamp`.
 * Reject requests older than 5 minutes.
 *
 * IMPORTANT: This function runs server-side only (uses node:crypto). Importing it into
 * the browser bundle will break the build via Node-only import — keep server-only.
 */
export async function verifyHubSpotSignature(args: {
  method: string;
  url: string;
  rawBody: string;
  timestamp: string;
  signature: string;
  clientSecret: string;
}): Promise<boolean> {
  const { createHmac } = await import('node:crypto');
  const tsMs = Number(args.timestamp);
  if (!Number.isFinite(tsMs)) return false;
  if (Math.abs(Date.now() - tsMs) > 5 * 60_000) return false;
  const source = `${args.method}${args.url}${args.rawBody}${args.timestamp}`;
  const computed = createHmac('sha256', args.clientSecret).update(source, 'utf8').digest('base64');
  return timingSafeEq(computed, args.signature);
}

function timingSafeEq(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let mismatch = 0;
  for (let i = 0; i < a.length; i++) mismatch |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return mismatch === 0;
}
